import { setAppLoader } from '@/redux/common/CommonActions';
import { useGetDefaultCustomerSettings } from '@/redux/hooks/useCustomerSettings';
import { Col, Divider, Form, Radio, Row, Spin } from 'antd';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useIs12HoursTimeFormat from '../../../../../../redux/hooks/useIs12HoursTimeFormat';
import useShipmentEditPermission from '../../../../../../redux/hooks/useShipmentEditPermission';
import {
  getCustomerRoutingSettingsInProgress,
  shipmentSpotBidInProgress,
} from '../../../../../../redux/shipment/ShipmentActions';
import { valueToNumber, valueToString } from '../../../../../../utilz';
import { routeFileSelector } from '../../../../../../utilz/i18n';
import Button from '../../../../../components/Buttons/Button';
import FormCheckBox from '../../../../../components/Inputs/FormCheckBox';
import FormDatePicker from '../../../../../components/Inputs/FormDatePicker';
import FormInput from '../../../../../components/Inputs/FormInput';
import FormTimePicker from '../../../../../components/Inputs/FormTimePicker';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../../components/Labels/FormLabel';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import SecondaryLabel from '../../../../../components/Labels/SecondaryLabel';
import SpotBidExpireTimeSelector from '../../../../../components/Selectors/SpotBidExpireTimeSelector';
import TimeZoneSelectorTMS from '../../../../../components/Selectors/TimeZoneSelectorTMS';
import SportBidRouting from './SportBidRouting';

const SportBidForm = ({ handleSpotBidSuccess, submitSpotBidButtonContainerClassName }) => {
  const dispatch = useDispatch();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const { shipmentDetails, customerRoutingsSettings } = useSelector((state) => state.shipment);
  const { user, selectedCompany } = useSelector((state) => state.auth);
  const {
    selectedCompany: { companyId },
  } = useSelector((state) => state.auth);
  const spotBidButtonDisabled = useMemo(() => selectedProviders.length === 0, [selectedProviders]);
  const [form] = Form.useForm();

  const { data: defaultSpotBidHours } = useGetDefaultCustomerSettings(companyId, 'Shipment', 'IsSetDefaultBidDateHour');

  useEffect(() => {
    if (defaultSpotBidHours && defaultSpotBidHours.length > 0) {
      const bidTime = defaultSpotBidHours[0]?.defaultValue;
      if (bidTime)
        form.setFieldsValue({ OptionValueText: 'Hours', tenderDate: moment(new Date()), spotBidTime: bidTime });
    }
  }, [defaultSpotBidHours]);

  const handleSelectedSpotProviders = (data) => {
    setSelectedProviders(data);
  };
  const handleSubmit = (data) => {
    dispatch(setAppLoader(true));
    const carrierKey = String(shipmentDetails?.data?.carrierKey).length > 0 ? shipmentDetails?.data?.carrierKey : 0;
    const payload = {};
    payload.shipmentKey = shipmentDetails.data?.shipmentKey;
    payload.carrierKey = String(carrierKey);
    payload.rateShopCost = valueToString(shipmentDetails.data?.rateShopCost);
    payload.isReset = String(Number(Boolean(data.isReset)));
    payload.isSendMail = Number(!data.isSendMail);
    payload.spotBidTime = valueToString(data.spotBidTime);
    payload.spotBidTimeText = valueToString(data.spotBidTimeText);
    if (data.OptionValueText === 'Time') {
      payload.tenderDateTime = `${data.timeTenderDate.format('MM/DD/yyyy')} ${data.timeTenderTime.format('h:mm A')}`;
      payload.tenderDate = '';
    } else {
      payload.tenderDate = data.tenderDate?.format('MM/DD/yyyy');
      payload.tenderDateTime = '';
    }
    payload.userId = user.data.id;
    payload.customerKey = shipmentDetails.data?.customerKey;
    payload.spotBidTimeZoneValue = valueToString(data.SpotBidTimeZoneValue);
    payload.optionValueText = valueToString(data.OptionValueText);
    payload.spotBidType = null;
    payload.spotBidCarrierEmailList = selectedProviders.map((row) => ({
      carrierKey: row.carrierKey,
      emailAddress: row.tenderEmailAddress,
    }));
    payload.routingType = Number(data.routingType);
    payload.targetPrice = valueToNumber(data.targetPrice);

    const formData = new FormData();
    for (const key in payload) {
      if (Object.hasOwnProperty.call(payload, key)) {
        formData.append(key, payload[key]);
      }
    }
    payload.callBackFun = () => {
      handleSpotBidSuccess();
    };
    dispatch(shipmentSpotBidInProgress(payload));
  };

  const handleRadioChange = ({ target: { value } }) => {
    form.setFieldsValue({ OptionValueText: value });
  };

  const optionValueText = Form.useWatch('OptionValueText', form);

  useEffect(() => {
    if (!customerRoutingsSettings?.success && selectedCompany?.companyId) {
      dispatch(
        getCustomerRoutingSettingsInProgress({
          Customerkey: selectedCompany?.companyId,
        }),
      );
    }
  }, [customerRoutingsSettings?.success, selectedCompany]);

  useEffect(() => {
    if (customerRoutingsSettings?.success) {
      form.setFieldsValue({
        adminEmail: customerRoutingsSettings?.data?.adminEmail,
      });
    }
  }, [customerRoutingsSettings?.success]);

  const hasEditPermission = useShipmentEditPermission();
  const submitButtonRef = useRef(null);

  const is12HoursFormat = useIs12HoursTimeFormat();

  return (
    <Form
      onFinish={handleSubmit}
      form={form}
      className="h-100 d-flex flex-direction-column"
      initialValues={{ routingType: 0 }}
    >
      <div className="py-15">
        <Row className="mb-10" gutter={15}>
          <Col sm={24} md={6}>
            <Spin size="small" spinning={customerRoutingsSettings?.progressing}>
              <FormInput
                label={t('translation:shipmentAdd.rateShopDetails.adminEmail')}
                editMode={false}
                name="adminEmail"
                margin="m-0"
              />
            </Spin>
          </Col>
          <Col sm={24} md={6}>
            <FormCheckBox
              checkBoxClass="normal-checkbox"
              label={t('translation:shipmentAdd.spotBid.doNotSendEmail')}
              name="isSendMail"
            />
          </Col>
          <Col sm={24} md={6}>
            <FormCheckBox
              checkBoxClass="normal-checkbox"
              label={t('translation:shipmentAdd.spotBid.resetSpotBid')}
              name="isReset"
            />
          </Col>
        </Row>
        <Radio.Group className="w-100" value={optionValueText}>
          <Row gutter={15} className="position-relative">
            <FormLabel className=" mb-15 spot-bid-expiry-time-label">
              {t('translation:shipmentAdd.spotBid.expirationTime.label')}
            </FormLabel>
            <Col sm={24} md={6}>
              <div className="mt-20 d-flex align-items-center">
                <Radio name="OptionValueText" onChange={handleRadioChange} value="Hours">
                  <SecondaryLabel className="mt-n1 f-12 fw-600 text-dark lh-0">
                    {t('translation:shipmentAdd.spotBid.dateHours')}
                  </SecondaryLabel>
                </Radio>
              </div>
            </Col>
            <Col sm={24} md={6} className={`${optionValueText !== 'Hours' && 'spot-bid-disabled-row'}`}>
              <FormDatePicker
                checkBoxClass="normal-checkbox"
                label={t('translation:default.fields.createdOn.placeholder')}
                name="tenderDate"
                rules={[
                  {
                    required: optionValueText === 'Hours',
                    message: t('translation:shipmentAdd.spotBid.selectDate'),
                  },
                ]}
              />
            </Col>
            <Col sm={24} md={6} className={`${optionValueText !== 'Hours' && 'spot-bid-disabled-row'}`}>
              <SpotBidExpireTimeSelector
                checkBoxClass="normal-checkbox"
                label={t('translation:default.fields.hours.placeholder')}
                name="spotBidTime"
                rules={[
                  {
                    required: optionValueText === 'Hours',
                    message: t('translation:default.fields.hours.message'),
                  },
                ]}
                onChange={(val) => form.setFieldsValue({ spotBidTimeText: val.label })}
              />
            </Col>
            <Col sm={24} md={6}>
              <FormInput label="Ceiling Rate" margin="m-0" name="targetPrice" editMode inputType="number" />
            </Col>
          </Row>
          <Row gutter={15}>
            <Col sm={24} md={6}>
              <div className="mt-20 d-flex align-items-center">
                <Radio name="OptionValueText" onChange={handleRadioChange} value="Time">
                  <SecondaryLabel className="mt-n1 f-12 fw-600 text-dark lh-0">
                    {t('translation:shipment.drawer.shipmentTransport.dateTime')}
                  </SecondaryLabel>
                </Radio>
              </div>
            </Col>
            <Col sm={24} md={6} className={`${optionValueText !== 'Time' && 'spot-bid-disabled-row'}`}>
              <FormDatePicker
                label={t('translation:default.fields.createdOn.placeholder')}
                name="timeTenderDate"
                margin="m-0"
                rules={[
                  {
                    required: optionValueText === 'Time',
                    message: t('translation:shipment.drawer.shipmentTransport.requestedDateTime.date_message'),
                  },
                ]}
              />
            </Col>
            <Col sm={24} md={6} className={`${optionValueText !== 'Time' && 'spot-bid-disabled-row'}`}>
              <FormTimePicker
                use12Hours={is12HoursFormat}
                label={t('translation:default.fields.selectTime')}
                name="timeTenderTime"
                editMode
                margin="m-0"
                rules={[
                  {
                    required: optionValueText === 'Time',
                    message: t('translation:shipment.drawer.shipmentTransport.requestedDateTime.time_message'),
                  },
                ]}
              />
            </Col>
            <Col sm={24} md={6} className={`${optionValueText !== 'Time' && 'spot-bid-disabled-row'}`}>
              <TimeZoneSelectorTMS
                label={t('translation:default.fields.timeZone.placeholder')}
                name="SpotBidTimeZoneValue"
                editMode
                margin="m-0"
                rules={[
                  {
                    required: optionValueText === 'Time',
                    message: t('translation:default.fields.timeZone.message'),
                  },
                ]}
              />
            </Col>
          </Row>
        </Radio.Group>
        <HiddenFormInput
          name="OptionValueText"
          rules={[
            {
              required: true,
              message: t('translation:shipmentAdd.spotBid.expirationTime.message'),
            },
          ]}
        />
      </div>
      <Divider className="my-15" />
      <Row>
        <Col>
          <PrimaryLabel className="f-18">{t('translation:shipmentAdd.spotBid.routingSpotBidList')}</PrimaryLabel>
        </Col>
      </Row>
      <Divider className="my-15" />
      <div className="overflow-hidden h-100">
        <SportBidRouting handleSelectedSpotProviders={handleSelectedSpotProviders} />
      </div>
      <div
        style={{
          height: submitButtonRef?.current?.clientHeight ? submitButtonRef?.current?.clientHeight : 0,
        }}
      />
      <Row
        justify="end"
        className={`submit-spot-bid-button-container ${
          submitSpotBidButtonContainerClassName ? submitSpotBidButtonContainerClassName : ''
        }`}
        ref={submitButtonRef}
      >
        <Col>
          <Button disabled={spotBidButtonDisabled || !hasEditPermission} variant="success" buttonType="submit">
            {t('translation:shipmentAdd.spotBid.submitSpotBid')}
          </Button>
        </Col>
      </Row>
      <HiddenFormInput name="spotBidTimeText" />
      <HiddenFormInput name="routingType" />
    </Form>
  );
};
export default SportBidForm;
